import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-jalaali';
import { toJalali, toGregorian } from '../../utils/jalali';
import { cn } from '../../lib/utils';
import Icon from './Icon';

moment.loadPersian({ dialect: 'persian-modern' });

const MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];

const WEEK_DAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

export default function JalaliDatePicker({
  value,
  onChange,
  label,
  error,
  placeholder = 'انتخاب تاریخ',
  className,
  required = false,
  disabled = false,
  minDate,
  maxDate,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());

  const [pickerMode, setPickerMode] = useState('calendar');

  const wrapperRef = useRef(null);

  // ---------------------------------------------
  // مقدار اولیه
  // ---------------------------------------------

  useEffect(() => {
    if (!value) {
      setDisplayValue('');
      setSelectedDate(null);
      setViewDate(new Date());
      return;
    }

    const dateObj = value instanceof Date ? value : new Date(value);

    if (!isNaN(dateObj.getTime())) {
      setDisplayValue(toJalali(dateObj));
      setSelectedDate(dateObj);
      setViewDate(dateObj);
    }
  }, [value]);

  // ---------------------------------------------
  // بستن با کلیک بیرون
  // ---------------------------------------------

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setPickerMode('calendar');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ---------------------------------------------
  // اطلاعات تاریخ فعلی تقویم
  // ---------------------------------------------

  const getViewInfo = () => {
    const baseDate = viewDate || selectedDate || new Date();
    const m = moment(baseDate);

    return {
      year: Number(m.format('jYYYY')),
      month: Number(m.format('jMM')),
      day: Number(m.format('jDD')),
    };
  };

  const { year: viewYear, month: viewMonth, day: viewDay } =
    getViewInfo();

  // ---------------------------------------------
  // انتخاب تاریخ
  // ---------------------------------------------

  const handleDateSelect = (day) => {
    const jalaliDate = `${viewYear}/${String(viewMonth).padStart(
      2,
      '0'
    )}/${String(day).padStart(2, '0')}`;

    const gregorian = toGregorian(jalaliDate);

    if (!gregorian) return;

    if (minDate && gregorian < minDate) return;
    if (maxDate && gregorian > maxDate) return;

    setDisplayValue(jalaliDate);
    setSelectedDate(gregorian);
    setViewDate(gregorian);

    onChange?.(gregorian);

    setIsOpen(false);
    setPickerMode('calendar');
  };

  // ---------------------------------------------
  // امروز
  // ---------------------------------------------

  const handleToday = () => {
    const now = new Date();

    if (minDate && now < minDate) return;
    if (maxDate && now > maxDate) return;

    setDisplayValue(toJalali(now));
    setSelectedDate(now);
    setViewDate(now);

    onChange?.(now);

    setIsOpen(false);
    setPickerMode('calendar');
  };

  // ---------------------------------------------
  // پاک کردن
  // ---------------------------------------------

  const handleClear = () => {
    setDisplayValue('');
    setSelectedDate(null);
    setViewDate(new Date());

    onChange?.(null);

    setPickerMode('calendar');
    setIsOpen(false);
  };

  // ---------------------------------------------
  // تغییر ماه
  // ---------------------------------------------

  const changeMonth = (delta) => {
    const m = moment(viewDate || new Date());

    m.add(delta, 'jMonth');

    setViewDate(m.toDate());
  };

  // ---------------------------------------------
  // تغییر سال
  // ---------------------------------------------

  const changeYear = (delta) => {
    const m = moment(viewDate || new Date());

    m.add(delta, 'jYear');

    setViewDate(m.toDate());
  };

  // ---------------------------------------------
  // انتخاب ماه
  // ---------------------------------------------

  const selectMonth = (month) => {
    const m = moment(viewDate || new Date());

    const currentYear = Number(m.format('jYYYY'));

    const newDate = moment(
      `${currentYear}/${String(month).padStart(2, '0')}/01`,
      'jYYYY/jMM/jDD'
    ).toDate();

    setViewDate(newDate);
    setPickerMode('calendar');
  };

  // ---------------------------------------------
  // انتخاب سال
  // ---------------------------------------------

  const selectYear = (year) => {
    const m = moment(viewDate || new Date());

    const currentMonth = Number(m.format('jMM'));
    const currentDay = Number(m.format('jDD'));

    const maxDay = moment(
      `${year}/${String(currentMonth).padStart(2, '0')}/01`,
      'jYYYY/jMM/jDD'
    ).daysInMonth();

    const safeDay = Math.min(currentDay, maxDay);

    const newDate = moment(
      `${year}/${String(currentMonth).padStart(
        2,
        '0'
      )}/${String(safeDay).padStart(2, '0')}`,
      'jYYYY/jMM/jDD'
    ).toDate();

    setViewDate(newDate);
    setPickerMode('calendar');
  };

  // ---------------------------------------------
  // سال‌های قابل انتخاب
  // ---------------------------------------------

  const currentJYear = Number(moment().format('jYYYY'));

  const years = Array.from(
    { length: 121 },
    (_, index) => currentJYear - 100 + index
  );

  // ---------------------------------------------
  // ساخت روزهای ماه
  // ---------------------------------------------

  const firstDay = moment(
    `${viewYear}/${String(viewMonth).padStart(2, '0')}/01`,
    'jYYYY/jMM/jDD'
  );

  const daysInMonth = firstDay.daysInMonth();

  const startDay = firstDay.day();

  const days = [];

  for (let i = 0; i < startDay; i++) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  // ---------------------------------------------
  // بررسی قابل انتخاب بودن روز
  // ---------------------------------------------

  const isDaySelectable = (day) => {
    if (!day) return false;

    const jalaliDate = `${viewYear}/${String(viewMonth).padStart(
      2,
      '0'
    )}/${String(day).padStart(2, '0')}`;

    const gregorian = toGregorian(jalaliDate);

    if (!gregorian) return false;

    if (minDate && gregorian < minDate) return false;
    if (maxDate && gregorian > maxDate) return false;

    return true;
  };

  // ---------------------------------------------
  // بررسی امروز
  // ---------------------------------------------

  const todayJalali = moment().format('jYYYY/jMM/jDD');

  // ---------------------------------------------
  // بررسی تاریخ انتخاب شده
  // ---------------------------------------------

  const selectedJalali = selectedDate
    ? moment(selectedDate).format('jYYYY/jMM/jDD')
    : '';

  // ---------------------------------------------
  // رندر انتخاب سال
  // ---------------------------------------------

  const renderYearPicker = () => {
    return (
      <div className="p-3">

        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setPickerMode('calendar')}
            className="flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-bold text-slate-500 transition hover:bg-slate-100"
          >
            <Icon
              name="arrow-right"
              className="h-4 w-4"
            />

            بازگشت
          </button>

          <span className="text-sm font-black text-slate-800">
            انتخاب سال
          </span>

          <span className="rounded-lg bg-emerald-50 px-2 py-1 text-xs font-black text-emerald-600">
            {viewYear}
          </span>
        </div>

        <div className="grid max-h-72 grid-cols-4 gap-2 overflow-y-auto p-1">
          {years.map((year) => {
            const selected = year === viewYear;

            return (
              <button
                key={year}
                type="button"
                onClick={() => selectYear(year)}
                className={cn(
                  'rounded-xl px-2 py-2.5 text-sm font-bold transition-all',
                  selected
                    ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                    : 'bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                )}
              >
                {year}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------------------------------------------
  // رندر انتخاب ماه
  // ---------------------------------------------

  const renderMonthPicker = () => {
    return (
      <div className="p-3">

        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setPickerMode('calendar')}
            className="flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-bold text-slate-500 transition hover:bg-slate-100"
          >
            <Icon
              name="arrow-right"
              className="h-4 w-4"
            />

            بازگشت
          </button>

          <span className="text-sm font-black text-slate-800">
            انتخاب ماه
          </span>

          <span className="rounded-lg bg-emerald-50 px-2 py-1 text-xs font-black text-emerald-600">
            {viewYear}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((month, index) => {
            const monthNumber = index + 1;
            const selected = monthNumber === viewMonth;

            return (
              <button
                key={month}
                type="button"
                onClick={() => selectMonth(monthNumber)}
                className={cn(
                  'rounded-xl px-2 py-3 text-xs font-bold transition-all',
                  selected
                    ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                    : 'bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:text-emerald-700'
                )}
              >
                {month}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------------------------------------------
  // رندر تقویم
  // ---------------------------------------------

  const renderCalendar = () => {
    return (
      <div className="p-3">

        {/* تاریخ انتخاب شده */}
        <div className="mb-3 rounded-2xl bg-gradient-to-l from-emerald-500 to-emerald-600 p-3 text-white shadow-lg shadow-emerald-500/20">

          <div className="text-[10px] font-medium opacity-80">
            تاریخ انتخاب شده
          </div>

          <div className="mt-1 text-lg font-black tracking-wide">
            {displayValue || 'هنوز انتخاب نشده'}
          </div>

        </div>

        {/* Header */}

        <div className="mb-3 flex items-center justify-between">

          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="grid h-9 w-9 place-items-center rounded-xl bg-slate-50 text-slate-500 transition hover:bg-emerald-50 hover:text-emerald-600"
            title="ماه قبل"
          >
            <Icon
              name="chevronRight"
              className="h-4 w-4"
            />
          </button>

          <div className="flex items-center gap-1">

            <button
              type="button"
              onClick={() => setPickerMode('month')}
              className="rounded-xl px-3 py-2 text-sm font-black text-slate-800 transition hover:bg-emerald-50 hover:text-emerald-600"
            >
              {MONTHS[viewMonth - 1]}
            </button>

            <button
              type="button"
              onClick={() => setPickerMode('year')}
              className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-black text-slate-800 transition hover:bg-emerald-50 hover:text-emerald-600"
            >
              {viewYear}
            </button>

          </div>

          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="grid h-9 w-9 place-items-center rounded-xl bg-slate-50 text-slate-500 transition hover:bg-emerald-50 hover:text-emerald-600"
            title="ماه بعد"
          >
            <Icon
              name="chevronLeft"
              className="h-4 w-4"
            />
          </button>

        </div>

        {/* روزهای هفته */}

        <div className="mb-1 grid grid-cols-7 gap-1">
          {WEEK_DAYS.map((day) => (
            <div
              key={day}
              className="py-1 text-center text-[11px] font-black text-slate-400"
            >
              {day}
            </div>
          ))}
        </div>

        {/* روزها */}

        <div className="grid grid-cols-7 gap-1">

          {days.map((day, index) => {

            if (!day) {
              return (
                <div
                  key={`empty-${index}`}
                  className="h-9"
                />
              );
            }

            const jalaliDate = `${viewYear}/${String(
              viewMonth
            ).padStart(2, '0')}/${String(day).padStart(
              2,
              '0'
            )}`;

            const selectable = isDaySelectable(day);

            const isToday = jalaliDate === todayJalali;

            const isSelected = jalaliDate === selectedJalali;

            return (
              <button
                key={day}
                type="button"
                disabled={!selectable}
                onClick={() => handleDateSelect(day)}
                className={cn(
                  'relative h-9 rounded-xl text-xs font-bold transition-all',

                  selectable &&
                    !isSelected &&
                    'text-slate-700 hover:bg-emerald-50 hover:text-emerald-700',

                  isSelected &&
                    'bg-emerald-500 text-white shadow-md shadow-emerald-500/30',

                  isToday &&
                    !isSelected &&
                    'ring-2 ring-emerald-300 ring-inset text-emerald-600',

                  !selectable &&
                    'cursor-not-allowed text-slate-300'
                )}
              >
                {day}

                {isToday && !isSelected && (
                  <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-emerald-500" />
                )}
              </button>
            );
          })}

        </div>

        {/* Footer */}

        <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">

          <button
            type="button"
            onClick={handleToday}
            className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-600 transition hover:bg-emerald-100"
          >
            امروز
          </button>

          <button
            type="button"
            onClick={handleClear}
            className="rounded-xl bg-red-50 px-3 py-2 text-xs font-black text-red-500 transition hover:bg-red-100"
          >
            پاک کردن
          </button>

        </div>
      </div>
    );
  };

  // ---------------------------------------------
  // Render
  // ---------------------------------------------

  return (
    <div
      ref={wrapperRef}
      className={cn('relative', className)}
    >

      {label && (
        <label className="mb-1.5 block text-sm font-bold text-slate-700">
          {label}

          {required && (
            <span className="mr-1 text-red-500">
              *
            </span>
          )}
        </label>
      )}

      <div className="relative">

        <input
          type="text"
          value={displayValue}
          readOnly
          onFocus={() => {
            if (!disabled) {
              setIsOpen(true);
              setPickerMode('calendar');
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full rounded-xl border-2 bg-white px-4 py-3 pl-12 text-sm font-medium text-slate-700 outline-none transition-all duration-300',

            'focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10',

            error
              ? 'border-red-500'
              : 'border-slate-200 hover:border-slate-300',

            disabled &&
              'cursor-not-allowed bg-slate-50 opacity-50'
          )}
        />

        {displayValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute left-10 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-red-500"
          >
            <Icon
              name="close"
              className="h-4 w-4"
            />
          </button>
        )}

        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              setIsOpen((prev) => !prev);
              setPickerMode('calendar');
            }
          }}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-emerald-500"
        >
          <Icon
            name="calendar"
            className="h-5 w-5"
          />
        </button>

      </div>

      {error && (
        <p className="mt-1 text-xs font-medium text-red-500">
          {error}
        </p>
      )}

      {isOpen && !disabled && (
        <div
          className="
            absolute
            z-50
            mt-2
            w-[310px]
            overflow-hidden
            rounded-2xl
            border
            border-slate-200
            bg-white
            shadow-2xl
            shadow-slate-900/10
          "
        >
          {pickerMode === 'calendar' && renderCalendar()}
          {pickerMode === 'year' && renderYearPicker()}
          {pickerMode === 'month' && renderMonthPicker()}
        </div>
      )}

    </div>
  );
}